import { useFormikContext } from 'formik';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  type DropResult,
  type ResponderProvided,
} from 'react-beautiful-dnd';
import { FaGripLines, FaTrash } from 'react-icons/fa';
import {
  AVOverviewItemTypeName,
  castEnumToOptions,
  Col,
  FieldSize,
  FormikSelect,
  FormikTextArea,
  FormikTimeInput,
  type IAVOverviewTemplateModel,
  Row,
  Show,
} from 'tno-core';

import * as styled from './styled';

export interface IOverviewGridProps {
  index: number;
}

/** OverviewGrid contains the table of items displayed for each overview section. */
export const OverviewGrid: React.FC<IOverviewGridProps> = ({ index }) => {
  const { values, setFieldValue } = useFormikContext<IAVOverviewTemplateModel>();

  const eveningOverviewItemTypeOptions = castEnumToOptions(AVOverviewItemTypeName);
  const items = values.sections[index].items;
  const DragDropContextAny = DragDropContext as any;
  const DroppableAny = Droppable as any;
  const DraggableAny = Draggable as any;

  /** function that runs after a user drops an item in the list */
  const handleDrop = (result: DropResult, provided: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    const updatedList = [...items];
    // Remove dragged item
    const [reorderedItem] = updatedList.splice(result.source.index, 1);
    // Add dropped item
    updatedList.splice(result.destination.index, 0, reorderedItem);
    // Update State
    setFieldValue(
      `sections.${index}.items`,
      updatedList.map((item, index) => ({ ...item, sortOrder: index })),
    );
  };

  const handleDeleteItem = (itemIndex: number) => {
    const rows = [...items];
    rows.splice(itemIndex, 1);
    setFieldValue(
      `sections.${index}.items`,
      rows.map((item, index) => ({ ...item, sortOrder: index })),
    );
  };

  return (
    <styled.OverviewGrid>
      <Show visible={items.length === 0}>
        <div className="no-items">
          There are no items in this section, add a source then click "New story" to begin.
        </div>
      </Show>
      <Show visible={!(items.length === 0)}>
        <div className="grid">
          <Row className="header">
            <Col className="placement-header">Placement</Col>
            <Col className="time-header">Time</Col>
            <Col className="summary-header" flex="1">
              Summary
            </Col>
            <Col className="delete-header"></Col>
          </Row>
          <div className="contents">
            <DragDropContextAny onDragEnd={handleDrop}>
              <DroppableAny droppableId="list-container">
                {(provided: DroppableProvided) => (
                  <div
                    className="list-container"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {items.map((item, itemIndex) => (
                      <DraggableAny
                        key={itemIndex}
                        draggableId={itemIndex.toString()}
                        index={itemIndex}
                      >
                        {(provided: DraggableProvided) => (
                          <div
                            className="item-container"
                            ref={provided.innerRef}
                            key={itemIndex + 'item.id'}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                            <Row className="rows" key={itemIndex} nowrap>
                              <FaGripLines className="grip-lines" />
                              <FormikSelect
                                key={itemIndex + 'select'}
                                name={`sections.${index}.items.${itemIndex}.itemType`}
                                width={FieldSize.Small}
                                options={eveningOverviewItemTypeOptions}
                                value={eveningOverviewItemTypeOptions.find(
                                  (o) => o.value === item.itemType,
                                )}
                                isClearable={false}
                              />
                              <FormikTimeInput
                                key={itemIndex}
                                name={`sections.${index}.items.${itemIndex}.time`}
                                width={FieldSize.Small}
                                placeholder="hh:mm:ss"
                              />
                              <Col flex="1">
                                <FormikTextArea
                                  key={itemIndex + 'textarea'}
                                  name={`sections.${index}.items.${itemIndex}.summary`}
                                  rows={1}
                                />
                              </Col>
                              <FaTrash
                                className="clear-item"
                                key={itemIndex + 'trash'}
                                onClick={() => {
                                  handleDeleteItem(itemIndex);
                                }}
                              />
                            </Row>
                          </div>
                        )}
                      </DraggableAny>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </DroppableAny>
            </DragDropContextAny>
          </div>
        </div>
      </Show>
    </styled.OverviewGrid>
  );
};
